/**
 * Re-exported so consumers of a descriptor get the verb's type with it. It is
 * declared alongside the envelope key it travels in, since that is what a tool
 * actually writes.
 */
import type { ArtifactOrigin } from './artifact-constants';

export type { ArtifactOrigin };

/**
 * Abstract interface for Chat data (without TypeORM dependencies)
 */
export type ChatTriggerSource =
  | 'chat'
  | 'chat-bot'
  | 'slack'
  | 'linear'
  | 'webhook'
  | 'warehouse'
  | 'cron'
  | 'event'
  | 'dataset'
  | 'compaction-summary'
  | 'workflow'
  | 'system'
  | 'api';

export type ChatData = {
  id: string;
  orgId: string;
  actorId: string;
  triggerSource?: ChatTriggerSource | null;
  /** Legacy: agent identity from data graph. Prefer agentId for new agents. */
  agent: string | null;
  /** Agent ID from agents table. Links chat to a saved agent. */
  agentId?: string | null;
  title: string;
  status: 'active' | 'archived';
  parentId?: string | null;
  /** Parent conversation's tool call ID that spawned this sub-agent. Set when creating sub-agent chats. */
  parentToolCallId?: string | null;
  /** Folder ID when the chat is placed in a folder. Used by tools (e.g. create-agent) to place new agents in the same folder when no explicit folderId is passed. */
  folderId?: string | null;
  /** Resource ID for the chat when in a folder. */
  resourceId?: string | null;
  /** Org that owns the chat for credential/cross-org purposes. When in a folder, the folder's org; otherwise the org that created the chat (orgId). */
  ownerOrgId: string;
  metadata: {
    loadedToolIds?: string[];
    excludedToolKeys?: string[];
  } | null;
};

export type ChatModelData = {
  chatId: string;
  position: number;
  model: string;
};

/**
 * Chat action types
 */
export enum ChatActionType {
  ToolApproval = 'tool_approval',
  MessageQueue = 'message_queue',
  AddTool = 'add_tool',
  RemoveTool = 'remove_tool',
  InterruptedToolInput = 'interrupted_tool_input',
  InterruptedToolDataProvided = 'interrupted_tool_data_provided',
  LoadArtifact = 'load_artifact',
  /** Dismiss an artifact by id; suppresses it until something re-adds it. */
  RemoveArtifact = 'remove_artifact',
  /** Attach a resource (chat, agent, folder, trigger) to the conversation; see {@link ResourceType} in API. */
  LoadResource = 'load_resource',
  /** Remove a previously loaded resource from the conversation context. */
  UnloadResource = 'unload_resource',
  /** Attach a developer-defined Coco skill (instructions + bundled tools). */
  LoadSkill = 'load_skill',
  FocusAgent = 'focus_agent',
  UnfocusAgent = 'unfocus_agent',
  /** Persisted expected output type (EraQL text in action data); latest wins for {@link Conversation.outputShape}. */
  OutputShape = 'output_shape',
  AutoApprove = 'auto_approve',
  RemoveAutoApprove = 'remove_auto_approve',
  /** Turn on plan mode: load architecture skills + plan-first behavior. Latest enter/exit wins. */
  EnterPlanMode = 'enter_plan_mode',
  /** Turn off plan mode. */
  ExitPlanMode = 'exit_plan_mode',
  /** Register an ad-hoc client-implemented (interruptable) tool definition; action data is an {@link InterruptableToolDefinition}. */
  RegisterInterruptableTool = 'register_interruptable_tool',
}

/** Reserved tool-key provider prefix for ad-hoc client-implemented tools (e.g. `client/save-artifacts`). */
export const INTERRUPTABLE_TOOL_KEY_PREFIX = 'client/';

/**
 * Ad-hoc tool definition supplied by the caller (e.g. the web client) rather
 * than implemented in the codebase. Executing such a tool suspends the run
 * (incomplete tool call) until the client submits the result via the
 * run-interrupted endpoint.
 */
export type InterruptableToolDefinition = {
  /** Tool key namespaced under `client/` (see {@link INTERRUPTABLE_TOOL_KEY_PREFIX}). */
  toolKey: string;
  name: string;
  description: string;
  /** JSON schema describing the tool input. */
  inputSchema: Record<string, unknown>;
};

/**
 * Chat action status
 */
export type ChatActionStatus =
  | 'pending' // Action is waiting for user response
  | 'approved' // Action has been approved (for tool approvals)
  | 'denied' // Action has been denied (for tool approvals)
  | 'completed' // Action is complete
  | 'cancelled'; // Action was cancelled

/**
 * Abstract interface for ChatAction data (without TypeORM dependencies)
 */
export type ChatActionData = {
  id: string;
  /** Owning user (chat actor); stored on `chat_actions` with `chatId` (no org column). */
  actorId: string | null;
  chatId: string;
  type: ChatActionType;
  status: ChatActionStatus;
  data: Record<string, unknown> | null;
  completedAt: Date | null;
  approvedAt: Date | null;
  approvedBy: string | null;
  createdAt: Date;
  updatedAt: Date;
};

/**
 * Tool loader interface to abstract ToolSubsystem
 */
export type ToolLoader = {
  loadTools: (
    toolIds: string[],
    /**
     * `userId` is the acting user. Loaders that resolve org-scoped resources
     * through an actor context (e.g. the workflow tool loader) refuse to
     * resolve without it, so omitting it makes those tool keys unresolvable.
     */
    context: { orgId: string; userId?: string }
  ) => Promise<
    Array<{
      key: string;
      canExecute: () => boolean;
    }>
  >;
};

/**
 * Descriptor for an artifact in a conversation.
 * Used by `Conversation.artifacts()` (async) and artifact panel rendering.
 */
export type ArtifactDescriptor = {
  type: string;
  artifactId: string;
  payload: unknown;
  messageId: string;
  toolCallId: string;
  toolName: string;
  /**
   * What the conversation did to this artifact — made it, or changed one that
   * already existed. Absent when it did neither: opened it, ran it, or was
   * handed it by a mention.
   *
   * Not derivable from {@link ArtifactDescriptor.toolName} by a reader. Dedupe
   * keeps one descriptor per `artifactId`, and for a workflow that is whichever
   * call emitted the artifact envelope — the *open*, since the node and trigger
   * edits that followed return plain results. {@link Conversation.artifacts}
   * fills this in from every tool call in the chat instead, so an edit that
   * emitted nothing still counts.
   */
  origin?: ArtifactOrigin;
};

/**
 * Configuration for coco tool keys (can be overridden)
 */
export type CocoToolKeys = {
  /** Defaults to `coco/load-resource` when omitted. Legacy message parts may use `coco/load-agent`. */
  loadResource?: string;
  search: string;
  /** Google web search integration; always present in default Coco tool sets. */
  webSearch?: string;
  /** URL → markdown scrape; always present in default Coco tool sets. */
  siteScraper?: string;
};

/** Resource kinds aligned with the resource HTTP contract (`ResourceType`). */
export type ChatLoadedResourceType =
  | 'chat'
  | 'agent'
  | 'folder'
  | 'trigger'
  | 'workflow';

/** Active resource attachment derived from load_resource / unload_resource actions. */
export type LoadedResourceDescriptor = {
  resourceType: ChatLoadedResourceType;
  resourceId: string;
  /** Optional snapshot from `resource/get` at load time (for LLM context). */
  snapshot: unknown | undefined;
};

/** Loaded agent content for Coco active-context skills (from {@link AgentLoader}). */
export type LoadedAgentContent = {
  toolKey: string;
  agentId: string;
  systemPrompt: string;
};

/**
 * Loads saved agent instructions for agent tool keys (server-side only).
 */
export type AgentLoader = {
  loadAgents: (
    toolKeys: string[],
    orgId: string,
    userId: string
  ) => Promise<LoadedAgentContent[]>;
};

/** Resolved developer skill for LLM prompt injection (server-side only). */
export type ResolvedSkill = {
  skillKey: string;
  displayName: string;
  instructions: string;
  toolKeys: string[];
  /**
   * When true, inject this skill regardless of the chat trigger (mirrors
   * `CoteraSkill.triggerIndependent`). Non-chat triggers inject only these.
   */
  triggerIndependent: boolean;
};
